import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { getKnowledgeBaseSettings, updateKnowledgeBaseSettings } from '../services/knowledgeService';
import type { KnowledgeBaseTenantSettingsResponse, UpdateKnowledgeBaseSettingsRequest } from '../types/Knowledge';
import styles from './KnowledgeBaseSettingsPanel.module.css';

type SettingFieldKey = keyof UpdateKnowledgeBaseSettingsRequest;

type DraftState = {
  retrievalSemanticScoreThreshold: number;
  retrievalDefaultMaxChunks: number;
  retrievalMaxAllowedChunks: number;
  retrievalQueryMessageCount: number;
  retrievalEmbeddingQueryMaxChars: number;
  retrievalKeywordQueryMaxChars: number;
  retrievalMaxChunksPerDocument: number;
  retrievalNeighborChunks: number;
  retrievalMaxContextChars: number;
  retrievalCandidateMultiplier: number;
  retrievalHybridEnabled: boolean;
  retrievalQueryRewriteEnabled: boolean;
  retrievalRerankEnabled: boolean;
  retrievalRerankCandidateCount: number;
  retrievalFeedbackBoostEnabled: boolean;
  ingestionChunkMaxChars: number;
  ingestionChunkOverlapChars: number;
  ingestionPollIntervalSeconds: number;
  ingestionRetryMaxAttempts: number;
  ingestionRetryBackoffBaseSeconds: number;
  ingestionRetryBackoffMaxSeconds: number;
  ingestionStuckQueueAlertThresholdSeconds: number;
  ingestionAutoRepairEnabled: boolean;
  ingestionAutoRepairIntervalSeconds: number;
  ingestionAutoRepairMaxDocuments: number;
  ingestionAutoRepairIncludeFailedDocuments: boolean;
  ingestionAutoRepairIncludeUploadedDocuments: boolean;
  ingestionAutoRepairMinDocumentAgeMinutes: number;
  attachmentsEnabled: boolean;
  attachmentsMaxAttachmentsPerDocument: number;
  attachmentsMaxAttachmentBytes: number;
  attachmentsMaxVideoAttachmentBytes: number;
  attachmentsMaxCaptionLength: number;
  attachmentsMaxAltTextLength: number;
  attachmentsAllowedImageMimeTypesCsv: string;
  attachmentsAllowedVideoMimeTypesCsv: string;
};

type NumberFieldConfig = {
  key: Exclude<SettingFieldKey,
    'retrievalHybridEnabled'
    | 'retrievalQueryRewriteEnabled'
    | 'retrievalRerankEnabled'
    | 'retrievalFeedbackBoostEnabled'
    | 'ingestionAutoRepairEnabled'
    | 'ingestionAutoRepairIncludeFailedDocuments'
    | 'ingestionAutoRepairIncludeUploadedDocuments'
    | 'attachmentsEnabled'
    | 'attachmentsAllowedImageMimeTypesCsv'
    | 'attachmentsAllowedVideoMimeTypesCsv'>;
  section: 'retrieval' | 'ingestion' | 'autoRepair' | 'attachments';
  overrideKey: string;
  labelKey: string;
  helpKey: string;
  min: number;
  max: number;
  step?: number;
};

type BooleanFieldConfig = {
  key: Extract<SettingFieldKey,
    'retrievalHybridEnabled'
    | 'retrievalQueryRewriteEnabled'
    | 'retrievalRerankEnabled'
    | 'retrievalFeedbackBoostEnabled'
    | 'ingestionAutoRepairEnabled'
    | 'ingestionAutoRepairIncludeFailedDocuments'
    | 'ingestionAutoRepairIncludeUploadedDocuments'
    | 'attachmentsEnabled'>;
  section: 'retrieval' | 'autoRepair' | 'attachments';
  overrideKey: string;
  labelKey: string;
  helpKey: string;
};

type StringFieldConfig = {
  key: Extract<SettingFieldKey, 'attachmentsAllowedImageMimeTypesCsv' | 'attachmentsAllowedVideoMimeTypesCsv'>;
  section: 'attachments';
  overrideKey: string;
  labelKey: string;
  helpKey: string;
};

const numberFields: NumberFieldConfig[] = [
  {
    key: 'retrievalSemanticScoreThreshold',
    section: 'retrieval',
    overrideKey: 'Retrieval.SemanticScoreThreshold',
    labelKey: 'settings.fields.retrievalSemanticScoreThreshold.label',
    helpKey: 'settings.fields.retrievalSemanticScoreThreshold.help',
    min: 0,
    max: 1,
    step: 0.01,
  },
  {
    key: 'retrievalDefaultMaxChunks',
    section: 'retrieval',
    overrideKey: 'Retrieval.DefaultMaxChunks',
    labelKey: 'settings.fields.retrievalDefaultMaxChunks.label',
    helpKey: 'settings.fields.retrievalDefaultMaxChunks.help',
    min: 1,
    max: 100,
    step: 1,
  },
  {
    key: 'retrievalMaxAllowedChunks',
    section: 'retrieval',
    overrideKey: 'Retrieval.MaxAllowedChunks',
    labelKey: 'settings.fields.retrievalMaxAllowedChunks.label',
    helpKey: 'settings.fields.retrievalMaxAllowedChunks.help',
    min: 1,
    max: 100,
    step: 1,
  },
  {
    key: 'retrievalQueryMessageCount',
    section: 'retrieval',
    overrideKey: 'Retrieval.QueryMessageCount',
    labelKey: 'settings.fields.retrievalQueryMessageCount.label',
    helpKey: 'settings.fields.retrievalQueryMessageCount.help',
    min: 1,
    max: 10,
    step: 1,
  },
  {
    key: 'retrievalEmbeddingQueryMaxChars',
    section: 'retrieval',
    overrideKey: 'Retrieval.EmbeddingQueryMaxChars',
    labelKey: 'settings.fields.retrievalEmbeddingQueryMaxChars.label',
    helpKey: 'settings.fields.retrievalEmbeddingQueryMaxChars.help',
    min: 100,
    max: 4000,
    step: 1,
  },
  {
    key: 'retrievalKeywordQueryMaxChars',
    section: 'retrieval',
    overrideKey: 'Retrieval.KeywordQueryMaxChars',
    labelKey: 'settings.fields.retrievalKeywordQueryMaxChars.label',
    helpKey: 'settings.fields.retrievalKeywordQueryMaxChars.help',
    min: 50,
    max: 1000,
    step: 1,
  },
  {
    key: 'retrievalMaxChunksPerDocument',
    section: 'retrieval',
    overrideKey: 'Retrieval.MaxChunksPerDocument',
    labelKey: 'settings.fields.retrievalMaxChunksPerDocument.label',
    helpKey: 'settings.fields.retrievalMaxChunksPerDocument.help',
    min: 0,
    max: 10,
    step: 1,
  },
  {
    key: 'retrievalNeighborChunks',
    section: 'retrieval',
    overrideKey: 'Retrieval.NeighborChunks',
    labelKey: 'settings.fields.retrievalNeighborChunks.label',
    helpKey: 'settings.fields.retrievalNeighborChunks.help',
    min: 0,
    max: 3,
    step: 1,
  },
  {
    key: 'retrievalMaxContextChars',
    section: 'retrieval',
    overrideKey: 'Retrieval.MaxContextChars',
    labelKey: 'settings.fields.retrievalMaxContextChars.label',
    helpKey: 'settings.fields.retrievalMaxContextChars.help',
    min: 1000,
    max: 30000,
    step: 100,
  },
  {
    key: 'retrievalCandidateMultiplier',
    section: 'retrieval',
    overrideKey: 'Retrieval.CandidateMultiplier',
    labelKey: 'settings.fields.retrievalCandidateMultiplier.label',
    helpKey: 'settings.fields.retrievalCandidateMultiplier.help',
    min: 1,
    max: 10,
    step: 1,
  },
  {
    key: 'retrievalRerankCandidateCount',
    section: 'retrieval',
    overrideKey: 'Retrieval.RerankCandidateCount',
    labelKey: 'settings.fields.retrievalRerankCandidateCount.label',
    helpKey: 'settings.fields.retrievalRerankCandidateCount.help',
    min: 5,
    max: 50,
    step: 1,
  },
  {
    key: 'ingestionChunkMaxChars',
    section: 'ingestion',
    overrideKey: 'Ingestion.ChunkMaxChars',
    labelKey: 'settings.fields.ingestionChunkMaxChars.label',
    helpKey: 'settings.fields.ingestionChunkMaxChars.help',
    min: 300,
    max: 4000,
    step: 1,
  },
  {
    key: 'ingestionChunkOverlapChars',
    section: 'ingestion',
    overrideKey: 'Ingestion.ChunkOverlapChars',
    labelKey: 'settings.fields.ingestionChunkOverlapChars.label',
    helpKey: 'settings.fields.ingestionChunkOverlapChars.help',
    min: 0,
    max: 3999,
    step: 1,
  },
  {
    key: 'ingestionPollIntervalSeconds',
    section: 'ingestion',
    overrideKey: 'Ingestion.PollIntervalSeconds',
    labelKey: 'settings.fields.ingestionPollIntervalSeconds.label',
    helpKey: 'settings.fields.ingestionPollIntervalSeconds.help',
    min: 1,
    max: 600,
    step: 1,
  },
  {
    key: 'ingestionRetryMaxAttempts',
    section: 'ingestion',
    overrideKey: 'Ingestion.RetryMaxAttempts',
    labelKey: 'settings.fields.ingestionRetryMaxAttempts.label',
    helpKey: 'settings.fields.ingestionRetryMaxAttempts.help',
    min: 1,
    max: 20,
    step: 1,
  },
  {
    key: 'ingestionRetryBackoffBaseSeconds',
    section: 'ingestion',
    overrideKey: 'Ingestion.RetryBackoffBaseSeconds',
    labelKey: 'settings.fields.ingestionRetryBackoffBaseSeconds.label',
    helpKey: 'settings.fields.ingestionRetryBackoffBaseSeconds.help',
    min: 1,
    max: 3600,
    step: 1,
  },
  {
    key: 'ingestionRetryBackoffMaxSeconds',
    section: 'ingestion',
    overrideKey: 'Ingestion.RetryBackoffMaxSeconds',
    labelKey: 'settings.fields.ingestionRetryBackoffMaxSeconds.label',
    helpKey: 'settings.fields.ingestionRetryBackoffMaxSeconds.help',
    min: 1,
    max: 86400,
    step: 1,
  },
  {
    key: 'ingestionStuckQueueAlertThresholdSeconds',
    section: 'ingestion',
    overrideKey: 'Ingestion.StuckQueueAlertThresholdSeconds',
    labelKey: 'settings.fields.ingestionStuckQueueAlertThresholdSeconds.label',
    helpKey: 'settings.fields.ingestionStuckQueueAlertThresholdSeconds.help',
    min: 30,
    max: 86400,
    step: 1,
  },
  {
    key: 'ingestionAutoRepairIntervalSeconds',
    section: 'autoRepair',
    overrideKey: 'Ingestion.AutoRepairIntervalSeconds',
    labelKey: 'settings.fields.ingestionAutoRepairIntervalSeconds.label',
    helpKey: 'settings.fields.ingestionAutoRepairIntervalSeconds.help',
    min: 5,
    max: 86400,
    step: 1,
  },
  {
    key: 'ingestionAutoRepairMaxDocuments',
    section: 'autoRepair',
    overrideKey: 'Ingestion.AutoRepairMaxDocuments',
    labelKey: 'settings.fields.ingestionAutoRepairMaxDocuments.label',
    helpKey: 'settings.fields.ingestionAutoRepairMaxDocuments.help',
    min: 1,
    max: 1000,
    step: 1,
  },
  {
    key: 'ingestionAutoRepairMinDocumentAgeMinutes',
    section: 'autoRepair',
    overrideKey: 'Ingestion.AutoRepairMinDocumentAgeMinutes',
    labelKey: 'settings.fields.ingestionAutoRepairMinDocumentAgeMinutes.label',
    helpKey: 'settings.fields.ingestionAutoRepairMinDocumentAgeMinutes.help',
    min: 0,
    max: 10080,
    step: 1,
  },
  {
    key: 'attachmentsMaxAttachmentsPerDocument',
    section: 'attachments',
    overrideKey: 'Attachments.MaxAttachmentsPerDocument',
    labelKey: 'settings.fields.attachmentsMaxAttachmentsPerDocument.label',
    helpKey: 'settings.fields.attachmentsMaxAttachmentsPerDocument.help',
    min: 0,
    max: 200,
    step: 1,
  },
  {
    key: 'attachmentsMaxAttachmentBytes',
    section: 'attachments',
    overrideKey: 'Attachments.MaxAttachmentBytes',
    labelKey: 'settings.fields.attachmentsMaxAttachmentBytes.label',
    helpKey: 'settings.fields.attachmentsMaxAttachmentBytes.help',
    min: 1024,
    max: 100 * 1024 * 1024,
    step: 1,
  },
  {
    key: 'attachmentsMaxVideoAttachmentBytes',
    section: 'attachments',
    overrideKey: 'Attachments.MaxVideoAttachmentBytes',
    labelKey: 'settings.fields.attachmentsMaxVideoAttachmentBytes.label',
    helpKey: 'settings.fields.attachmentsMaxVideoAttachmentBytes.help',
    min: 1024,
    max: 200 * 1024 * 1024,
    step: 1,
  },
  {
    key: 'attachmentsMaxCaptionLength',
    section: 'attachments',
    overrideKey: 'Attachments.MaxCaptionLength',
    labelKey: 'settings.fields.attachmentsMaxCaptionLength.label',
    helpKey: 'settings.fields.attachmentsMaxCaptionLength.help',
    min: 0,
    max: 2000,
    step: 1,
  },
  {
    key: 'attachmentsMaxAltTextLength',
    section: 'attachments',
    overrideKey: 'Attachments.MaxAltTextLength',
    labelKey: 'settings.fields.attachmentsMaxAltTextLength.label',
    helpKey: 'settings.fields.attachmentsMaxAltTextLength.help',
    min: 0,
    max: 2000,
    step: 1,
  },
];

const booleanFields: BooleanFieldConfig[] = [
  {
    key: 'retrievalHybridEnabled',
    section: 'retrieval',
    overrideKey: 'Retrieval.HybridEnabled',
    labelKey: 'settings.fields.retrievalHybridEnabled.label',
    helpKey: 'settings.fields.retrievalHybridEnabled.help',
  },
  {
    key: 'retrievalQueryRewriteEnabled',
    section: 'retrieval',
    overrideKey: 'Retrieval.QueryRewriteEnabled',
    labelKey: 'settings.fields.retrievalQueryRewriteEnabled.label',
    helpKey: 'settings.fields.retrievalQueryRewriteEnabled.help',
  },
  {
    key: 'retrievalRerankEnabled',
    section: 'retrieval',
    overrideKey: 'Retrieval.RerankEnabled',
    labelKey: 'settings.fields.retrievalRerankEnabled.label',
    helpKey: 'settings.fields.retrievalRerankEnabled.help',
  },
  {
    key: 'retrievalFeedbackBoostEnabled',
    section: 'retrieval',
    overrideKey: 'Retrieval.FeedbackBoostEnabled',
    labelKey: 'settings.fields.retrievalFeedbackBoostEnabled.label',
    helpKey: 'settings.fields.retrievalFeedbackBoostEnabled.help',
  },
  {
    key: 'ingestionAutoRepairEnabled',
    section: 'autoRepair',
    overrideKey: 'Ingestion.AutoRepairEnabled',
    labelKey: 'settings.fields.ingestionAutoRepairEnabled.label',
    helpKey: 'settings.fields.ingestionAutoRepairEnabled.help',
  },
  {
    key: 'ingestionAutoRepairIncludeFailedDocuments',
    section: 'autoRepair',
    overrideKey: 'Ingestion.AutoRepairIncludeFailedDocuments',
    labelKey: 'settings.fields.ingestionAutoRepairIncludeFailedDocuments.label',
    helpKey: 'settings.fields.ingestionAutoRepairIncludeFailedDocuments.help',
  },
  {
    key: 'ingestionAutoRepairIncludeUploadedDocuments',
    section: 'autoRepair',
    overrideKey: 'Ingestion.AutoRepairIncludeUploadedDocuments',
    labelKey: 'settings.fields.ingestionAutoRepairIncludeUploadedDocuments.label',
    helpKey: 'settings.fields.ingestionAutoRepairIncludeUploadedDocuments.help',
  },
  {
    key: 'attachmentsEnabled',
    section: 'attachments',
    overrideKey: 'Attachments.Enabled',
    labelKey: 'settings.fields.attachmentsEnabled.label',
    helpKey: 'settings.fields.attachmentsEnabled.help',
  },
];

const stringFields: StringFieldConfig[] = [
  {
    key: 'attachmentsAllowedImageMimeTypesCsv',
    section: 'attachments',
    overrideKey: 'Attachments.AllowedImageMimeTypesCsv',
    labelKey: 'settings.fields.attachmentsAllowedImageMimeTypesCsv.label',
    helpKey: 'settings.fields.attachmentsAllowedImageMimeTypesCsv.help',
  },
  {
    key: 'attachmentsAllowedVideoMimeTypesCsv',
    section: 'attachments',
    overrideKey: 'Attachments.AllowedVideoMimeTypesCsv',
    labelKey: 'settings.fields.attachmentsAllowedVideoMimeTypesCsv.label',
    helpKey: 'settings.fields.attachmentsAllowedVideoMimeTypesCsv.help',
  },
];

const allFieldKeys: SettingFieldKey[] = [
  ...numberFields.map(field => field.key),
  ...booleanFields.map(field => field.key),
  ...stringFields.map(field => field.key),
];

const overrideKeyByField = new Map<SettingFieldKey, string>([
  ...numberFields.map(field => [field.key, field.overrideKey] as const),
  ...booleanFields.map(field => [field.key, field.overrideKey] as const),
  ...stringFields.map(field => [field.key, field.overrideKey] as const),
]);

const toDraft = (response: KnowledgeBaseTenantSettingsResponse): DraftState => ({
  retrievalSemanticScoreThreshold: response.effective.retrieval.semanticScoreThreshold,
  retrievalDefaultMaxChunks: response.effective.retrieval.defaultMaxChunks,
  retrievalMaxAllowedChunks: response.effective.retrieval.maxAllowedChunks,
  retrievalQueryMessageCount: response.effective.retrieval.queryMessageCount,
  retrievalEmbeddingQueryMaxChars: response.effective.retrieval.embeddingQueryMaxChars,
  retrievalKeywordQueryMaxChars: response.effective.retrieval.keywordQueryMaxChars,
  retrievalMaxChunksPerDocument: response.effective.retrieval.maxChunksPerDocument,
  retrievalNeighborChunks: response.effective.retrieval.neighborChunks,
  retrievalMaxContextChars: response.effective.retrieval.maxContextChars,
  retrievalCandidateMultiplier: response.effective.retrieval.candidateMultiplier,
  retrievalHybridEnabled: response.effective.retrieval.hybridEnabled,
  retrievalQueryRewriteEnabled: response.effective.retrieval.queryRewriteEnabled,
  retrievalRerankEnabled: response.effective.retrieval.rerankEnabled,
  retrievalRerankCandidateCount: response.effective.retrieval.rerankCandidateCount,
  retrievalFeedbackBoostEnabled: response.effective.retrieval.feedbackBoostEnabled,
  ingestionChunkMaxChars: response.effective.ingestion.chunkMaxChars,
  ingestionChunkOverlapChars: response.effective.ingestion.chunkOverlapChars,
  ingestionPollIntervalSeconds: response.effective.ingestion.pollIntervalSeconds,
  ingestionRetryMaxAttempts: response.effective.ingestion.retryMaxAttempts,
  ingestionRetryBackoffBaseSeconds: response.effective.ingestion.retryBackoffBaseSeconds,
  ingestionRetryBackoffMaxSeconds: response.effective.ingestion.retryBackoffMaxSeconds,
  ingestionStuckQueueAlertThresholdSeconds: response.effective.ingestion.stuckQueueAlertThresholdSeconds,
  ingestionAutoRepairEnabled: response.effective.ingestion.autoRepairEnabled,
  ingestionAutoRepairIntervalSeconds: response.effective.ingestion.autoRepairIntervalSeconds,
  ingestionAutoRepairMaxDocuments: response.effective.ingestion.autoRepairMaxDocuments,
  ingestionAutoRepairIncludeFailedDocuments: response.effective.ingestion.autoRepairIncludeFailedDocuments,
  ingestionAutoRepairIncludeUploadedDocuments: response.effective.ingestion.autoRepairIncludeUploadedDocuments,
  ingestionAutoRepairMinDocumentAgeMinutes: response.effective.ingestion.autoRepairMinDocumentAgeMinutes,
  attachmentsEnabled: response.effective.attachments.enabled,
  attachmentsMaxAttachmentsPerDocument: response.effective.attachments.maxAttachmentsPerDocument,
  attachmentsMaxAttachmentBytes: response.effective.attachments.maxAttachmentBytes,
  attachmentsMaxVideoAttachmentBytes: response.effective.attachments.maxVideoAttachmentBytes,
  attachmentsMaxCaptionLength: response.effective.attachments.maxCaptionLength,
  attachmentsMaxAltTextLength: response.effective.attachments.maxAltTextLength,
  attachmentsAllowedImageMimeTypesCsv: response.effective.attachments.allowedImageMimeTypesCsv,
  attachmentsAllowedVideoMimeTypesCsv: response.effective.attachments.allowedVideoMimeTypesCsv,
});

const getOverrideFields = (response: KnowledgeBaseTenantSettingsResponse): Set<SettingFieldKey> => {
  const fields = new Set<SettingFieldKey>();

  for (const key of allFieldKeys) {
    const overrideKey = overrideKeyByField.get(key);
    if (overrideKey && Object.prototype.hasOwnProperty.call(response.overrides, overrideKey)) {
      fields.add(key);
    }
  }

  return fields;
};

const areEqual = (left: DraftState[SettingFieldKey], right: DraftState[SettingFieldKey]): boolean => left === right;

const statusClassNames: Record<'pending-reset' | 'pending-override' | 'overridden' | 'inherited', string> = {
  'pending-reset': styles.statusPendingReset,
  'pending-override': styles.statusPendingOverride,
  overridden: styles.statusOverridden,
  inherited: styles.statusInherited,
};

export const KnowledgeBaseSettingsPanel: React.FC = () => {
  const { t } = useTranslation('knowledge');
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canAdminSettings = hasPermission([PermissionCodes.adminAccess]);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<DraftState | null>(null);
  const [originalDraft, setOriginalDraft] = React.useState<DraftState | null>(null);
  const [originalOverrides, setOriginalOverrides] = React.useState<Set<SettingFieldKey>>(new Set());
  const [dirtyFields, setDirtyFields] = React.useState<Set<SettingFieldKey>>(new Set());
  const [pendingResetFields, setPendingResetFields] = React.useState<Set<SettingFieldKey>>(new Set());

  const loadSettings = React.useCallback(async () => {
    if (!canAdminSettings) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getKnowledgeBaseSettings();
      const nextDraft = toDraft(response);

      setDraft(nextDraft);
      setOriginalDraft(nextDraft);
      setOriginalOverrides(getOverrideFields(response));
      setDirtyFields(new Set());
      setPendingResetFields(new Set());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('settings.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [canAdminSettings, t]);

  React.useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const setFieldValue = <T extends SettingFieldKey>(key: T, value: DraftState[T]) => {
    setDraft(current => current ? { ...current, [key]: value } : current);

    setPendingResetFields(current => {
      if (!current.has(key)) {
        return current;
      }

      const next = new Set(current);
      next.delete(key);
      return next;
    });

    setDirtyFields(current => {
      const next = new Set(current);
      if (originalDraft && areEqual(value, originalDraft[key])) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const markFieldForReset = (key: SettingFieldKey) => {
    setPendingResetFields(current => {
      const next = new Set(current);
      next.add(key);
      return next;
    });

    setDirtyFields(current => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const cancelPendingReset = (key: SettingFieldKey) => {
    setPendingResetFields(current => {
      if (!current.has(key)) {
        return current;
      }

      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const validateDraft = (): string | null => {
    if (!draft) {
      return t('settings.loadFailed');
    }

    for (const field of numberFields) {
      if (pendingResetFields.has(field.key)) {
        continue;
      }

      const value = draft[field.key];
      if (value < field.min || value > field.max) {
        return t('settings.validation.range', {
          label: t(field.labelKey),
          min: field.min,
          max: field.max,
        });
      }
    }

    if (!pendingResetFields.has('retrievalDefaultMaxChunks')
      && !pendingResetFields.has('retrievalMaxAllowedChunks')
      && draft.retrievalDefaultMaxChunks > draft.retrievalMaxAllowedChunks) {
      return t('settings.validation.maxChunks');
    }

    if (!pendingResetFields.has('retrievalEmbeddingQueryMaxChars')
      && !pendingResetFields.has('retrievalKeywordQueryMaxChars')
      && draft.retrievalKeywordQueryMaxChars > draft.retrievalEmbeddingQueryMaxChars) {
      return t('settings.validation.queryChars');
    }

    if (!pendingResetFields.has('ingestionChunkMaxChars')
      && !pendingResetFields.has('ingestionChunkOverlapChars')
      && draft.ingestionChunkOverlapChars >= draft.ingestionChunkMaxChars) {
      return t('settings.validation.chunkOverlap');
    }

    return null;
  };

  const onSave = async () => {
    if (!draft) {
      return;
    }

    if (dirtyFields.size === 0 && pendingResetFields.size === 0) {
      showToast(t('settings.noChanges'), 'success');
      return;
    }

    const validationMessage = validateDraft();
    if (validationMessage) {
      showToast(validationMessage, 'error');
      return;
    }

    const payload: UpdateKnowledgeBaseSettingsRequest = {};
    for (const key of allFieldKeys) {
      if (pendingResetFields.has(key)) {
        payload[key] = null;
        continue;
      }

      if (dirtyFields.has(key)) {
        payload[key] = draft[key] as never;
      }
    }

    setSaving(true);
    try {
      const response = await updateKnowledgeBaseSettings(payload);
      const nextDraft = toDraft(response);

      setDraft(nextDraft);
      setOriginalDraft(nextDraft);
      setOriginalOverrides(getOverrideFields(response));
      setDirtyFields(new Set());
      setPendingResetFields(new Set());
      showToast(t('settings.saveSuccess'), 'success');
    } catch (saveError) {
      showToast(saveError instanceof Error ? saveError.message : t('settings.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const onResetAll = () => {
    if (!originalDraft) {
      return;
    }

    setDraft(originalDraft);
    setDirtyFields(new Set());
    setPendingResetFields(new Set());
  };

  const getFieldStatus = (key: SettingFieldKey): 'pending-reset' | 'pending-override' | 'overridden' | 'inherited' => {
    if (pendingResetFields.has(key)) {
      return 'pending-reset';
    }

    if (dirtyFields.has(key) && !originalOverrides.has(key)) {
      return 'pending-override';
    }

    if (originalOverrides.has(key)) {
      return 'overridden';
    }

    return 'inherited';
  };

  const renderResetAction = (key: SettingFieldKey, isOverridden: boolean) => {
    if (!isOverridden) {
      return null;
    }

    if (pendingResetFields.has(key)) {
      return (
        <button type="button" className={styles.inlineAction} onClick={() => cancelPendingReset(key)} disabled={saving}>
          {t('settings.keepOverride')}
        </button>
      );
    }

    return (
      <button type="button" className={styles.inlineAction} onClick={() => markFieldForReset(key)} disabled={saving}>
        {t('settings.resetField')}
      </button>
    );
  };

  const renderNumberField = (field: NumberFieldConfig) => {
    if (!draft) {
      return null;
    }

    const status = getFieldStatus(field.key);
    const isOverridden = originalOverrides.has(field.key);

    return (
      <label key={field.key} className={styles.fieldCard}>
        <div className={styles.fieldHead}>
          <span className={styles.fieldTitle}>{t(field.labelKey)}</span>
          <span className={`${styles.statusBadge} ${statusClassNames[status]}`}>
            {t(`settings.status.${status}`)}
          </span>
        </div>
        <span className={styles.fieldHelp}>{t(field.helpKey)}</span>
        <input
          type="number"
          min={field.min}
          max={field.max}
          step={field.step ?? 1}
          value={draft[field.key]}
          onChange={(event) => setFieldValue(field.key, Number(event.target.value))}
          disabled={saving}
        />
        <div className={styles.fieldFooter}>
          <span className={styles.fieldMeta}>{t('settings.range', { min: field.min, max: field.max })}</span>
          {renderResetAction(field.key, isOverridden)}
        </div>
      </label>
    );
  };

  const renderBooleanField = (field: BooleanFieldConfig) => {
    if (!draft) {
      return null;
    }

    const status = getFieldStatus(field.key);
    const isOverridden = originalOverrides.has(field.key);

    return (
      <div key={field.key} className={styles.fieldCard}>
        <div className={styles.fieldHead}>
          <span className={styles.fieldTitle}>{t(field.labelKey)}</span>
          <span className={`${styles.statusBadge} ${statusClassNames[status]}`}>
            {t(`settings.status.${status}`)}
          </span>
        </div>
        <span className={styles.fieldHelp}>{t(field.helpKey)}</span>
        <label className={styles.toggleRow}>
          <input
            type="checkbox"
            checked={draft[field.key]}
            onChange={(event) => setFieldValue(field.key, event.target.checked)}
            disabled={saving}
          />
          <span>{draft[field.key] ? t('settings.enabled') : t('settings.disabled')}</span>
        </label>
        <div className={styles.fieldFooter}>
          <span className={styles.fieldMeta}>{t('settings.booleanField')}</span>
          {renderResetAction(field.key, isOverridden)}
        </div>
      </div>
    );
  };

  const renderStringField = (field: StringFieldConfig) => {
    if (!draft) {
      return null;
    }

    const status = getFieldStatus(field.key);
    const isOverridden = originalOverrides.has(field.key);

    return (
      <label key={field.key} className={styles.fieldCard}>
        <div className={styles.fieldHead}>
          <span className={styles.fieldTitle}>{t(field.labelKey)}</span>
          <span className={`${styles.statusBadge} ${statusClassNames[status]}`}>
            {t(`settings.status.${status}`)}
          </span>
        </div>
        <span className={styles.fieldHelp}>{t(field.helpKey)}</span>
        <input
          type="text"
          value={draft[field.key]}
          onChange={(event) => setFieldValue(field.key, event.target.value)}
          disabled={saving}
        />
        <div className={styles.fieldFooter}>
          <span className={styles.fieldMeta}>{t('settings.stringField')}</span>
          {renderResetAction(field.key, isOverridden)}
        </div>
      </label>
    );
  };

  const hasChanges = dirtyFields.size > 0 || pendingResetFields.size > 0;

  if (!canAdminSettings) {
    return null;
  }

  return (
    <section className="company-section" style={{ marginTop: 16 }}>
      <div className={styles.panelHeader}>
        <div>
          <h3>{t('settings.title')}</h3>
          <p className={styles.panelIntro}>{t('settings.intro')}</p>
        </div>
        <div className={styles.panelActions}>
          <button type="button" className="btn btn-secondary" onClick={() => void loadSettings()} disabled={loading || saving}>
            {t('settings.reload')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onResetAll} disabled={loading || saving || !hasChanges}>
            {t('settings.discard')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void onSave()} disabled={loading || saving || !hasChanges}>
            {saving ? t('settings.saving') : t('settings.save')}
          </button>
        </div>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      {loading || !draft ? (
        <div className={styles.loadingWrap}>
          <Spinner text={t('settings.loading')} />
        </div>
      ) : (
        <div className={styles.sectionStack}>
          <section className={styles.settingsSection}>
            <div className={styles.sectionTitleWrap}>
              <h4 className={styles.sectionTitle}>{t('settings.sections.retrieval')}</h4>
              <p className={styles.sectionHelp}>{t('settings.sections.retrievalHelp')}</p>
            </div>
            <div className={styles.fieldsGrid}>
              {booleanFields.filter(field => field.section === 'retrieval').map(renderBooleanField)}
              {numberFields.filter(field => field.section === 'retrieval').map(renderNumberField)}
            </div>
          </section>

          <section className={styles.settingsSection}>
            <div className={styles.sectionTitleWrap}>
              <h4 className={styles.sectionTitle}>{t('settings.sections.ingestion')}</h4>
              <p className={styles.sectionHelp}>{t('settings.sections.ingestionHelp')}</p>
            </div>
            <div className={styles.fieldsGrid}>
              {numberFields.filter(field => field.section === 'ingestion').map(renderNumberField)}
            </div>
          </section>

          <section className={styles.settingsSection}>
            <div className={styles.sectionTitleWrap}>
              <h4 className={styles.sectionTitle}>{t('settings.sections.autoRepair')}</h4>
              <p className={styles.sectionHelp}>{t('settings.sections.autoRepairHelp')}</p>
            </div>
            <div className={styles.fieldsGrid}>
              {booleanFields.filter(field => field.section === 'autoRepair').map(renderBooleanField)}

              {numberFields.filter(field => field.section === 'autoRepair').map(renderNumberField)}
            </div>
          </section>

          <section className={styles.settingsSection}>
            <div className={styles.sectionTitleWrap}>
              <h4 className={styles.sectionTitle}>{t('settings.sections.attachments')}</h4>
              <p className={styles.sectionHelp}>{t('settings.sections.attachmentsHelp')}</p>
            </div>
            <div className={styles.fieldsGrid}>
              {booleanFields.filter(field => field.section === 'attachments').map(renderBooleanField)}
              {numberFields.filter(field => field.section === 'attachments').map(renderNumberField)}
              {stringFields.map(renderStringField)}
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

export default KnowledgeBaseSettingsPanel;